import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"
import consumer from "../../channels/consumer"
import { refreshWhenIdle } from "../../utils/turbo-refresh.util"

// Connects to data-controller="vehicles--extraction-status"
// Manages real-time vehicle extraction status updates via ActionCable.
// Handles both index page (Turbo Stream row updates) and detail/edit page
// (page reload when extraction completes, status badge updates).
export default class extends Controller {
  static values = {
    workspaceId: Number,
    vehicleId: Number,
    statusTexts: { type: Object, default: {} }
  }

  connect() {
    if (!this.hasWorkspaceIdValue) {
      return
    }

    this.isRefreshing = false
    this.pollTimer = null

    this.subscription = consumer.subscriptions.create(
      {
        channel: "VehicleExtractionChannel",
        workspace_id: this.workspaceIdValue
      },
      {
        received: this.received.bind(this)
      }
    )

    // After create with document upload, the redirect arrives before ActionCable
    // is subscribed. The ?extracting=true param signals a one-time delayed refresh
    // to pick up extraction results. The refresh goes to the clean URL (no param),
    // so it never runs again. It waits while the member's own click is in flight.
    if (this.hasVehicleIdValue && new URLSearchParams(window.location.search).has("extracting")) {
      this.pollTimer = setTimeout(() => {
        if (!this.isRefreshing) {
          this.isRefreshing = true
          const cleanUrl = window.location.pathname
          this.cancelRefresh = refreshWhenIdle(cleanUrl)
        }
      }, 3000)
    }
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
    this.cancelRefresh?.()
  }

  received(data) {
    // Handle Turbo Stream updates (index row replacement)
    if (data.type === "turbo_stream" && data.turbo_stream) {
      Turbo.renderStreamMessage(data.turbo_stream)

      // On vehicle detail page, refresh to load extracted data into the form
      // Only refresh on terminal states to avoid infinite loops
      if (data.vehicle_id && this.hasVehicleIdValue && data.vehicle_id === this.vehicleIdValue) {
        if ((data.status === "completed" || data.status === "failed") && !this.isRefreshing) {
          this.isRefreshing = true
          this.cancelRefresh = refreshWhenIdle(window.location.href)
        }
      }
      return
    }

    // Handle status-only updates for detail page
    const { vehicle_id } = data
    if (!vehicle_id) return

    const vehicleStatusEl = document.getElementById(`vehicle_${vehicle_id}_status`)

    if (vehicleStatusEl) {
      this.updateStatusIndicator(vehicleStatusEl, data.status, data.error, vehicle_id)

      const completedEl = document.getElementById(`vehicle_${vehicle_id}_completed`)
      if (completedEl && data.completed_at) {
        completedEl.innerText = data.completed_at
      }
    }
  }

  updateStatusIndicator(container, status, error, vehicleId) {
    container.innerHTML = ""

    const badge = this.createStatusBadge(status, error)
    container.appendChild(badge)

    if (status === "processing") {
      const pollButton = this.createPollButton(vehicleId)
      container.appendChild(pollButton)
    }

    if (status === "failed") {
      const retryButton = this.createRetryButton(vehicleId)
      container.appendChild(retryButton)
    }
  }

  createStatusBadge(status, error) {
    const badge = document.createElement("span")
    const badgeVariant = this.getBadgeVariant(status)
    badge.className = `badge badge--${badgeVariant}`
    badge.setAttribute("data-extraction-status", status)

    const escapedStatusText = this.escapeHtml(this.getStatusText(status))

    if (status === "processing") {
      badge.innerHTML = `
				<span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
				${escapedStatusText}
			`
    } else if (status === "completed") {
      badge.innerHTML = `
				<span class="material-symbols-outlined" style="font-size: 1rem; vertical-align: text-bottom;">check_circle</span>
				${escapedStatusText}
			`
    } else if (status === "failed" && error) {
      const escapedError = this.escapeHtml(error)
      badge.innerHTML = `
				<span class="material-symbols-outlined" style="font-size: 1rem; vertical-align: text-bottom;">error</span>
				${escapedStatusText}
			`
      badge.setAttribute("title", escapedError)
      badge.setAttribute("data-controller", "tooltip")
      badge.setAttribute("data-tooltip-title-value", escapedError)
    } else {
      badge.textContent = this.getStatusText(status)
    }

    return badge
  }

  createPollButton(vehicleId) {
    const form = document.createElement("form")
    form.className = "button_to"
    form.method = "post"
    form.action = `/${this.workspaceIdValue}/vehicles/${vehicleId}/poll_extraction`

    const button = document.createElement("button")
    button.type = "submit"
    button.className = "btn btn-link btn-sm p-0 ms-1"
    button.title = "Check extraction status"
    button.innerHTML = '<span class="material-symbols-outlined" style="font-size: 1rem;">refresh</span>'

    const csrfInput = document.createElement("input")
    csrfInput.type = "hidden"
    csrfInput.name = "authenticity_token"
    csrfInput.value = this.getCsrfToken()

    form.appendChild(button)
    form.appendChild(csrfInput)

    return form
  }

  createRetryButton(vehicleId) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "btn btn-sm btn-outline-danger ms-2"
    button.innerHTML = '<span class="material-symbols-outlined" style="font-size: 1rem;">refresh</span>'
    button.setAttribute("data-action", "click->vehicles--extraction-status#retry")
    button.setAttribute("data-vehicle-id", vehicleId)
    button.setAttribute("data-workspace-id", this.workspaceIdValue)
    button.setAttribute("title", "Retry")

    return button
  }

  async retry(event) {
    event.preventDefault()
    const button = event.currentTarget
    const vehicleId = button.getAttribute("data-vehicle-id")

    if (!vehicleId) return

    button.disabled = true
    button.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span>'

    try {
      const response = await fetch(`/${this.workspaceIdValue}/vehicles/${vehicleId}/retry_extraction`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      })

      if (!response.ok) {
        throw new Error("Retry failed")
      }

      setTimeout(() => {
        if (button.disabled) {
          button.disabled = false
          button.innerHTML = '<span class="material-symbols-outlined" style="font-size: 1rem;">refresh</span>'
        }
      }, 30000)
    } catch (error) {
      console.error("Failed to retry extraction:", error)
      button.disabled = false
      button.innerHTML = '<span class="material-symbols-outlined" style="font-size: 1rem;">refresh</span>'
    }
  }

  getBadgeVariant(status) {
    const variants = {
      pending: "default",
      processing: "info",
      completed: "success",
      failed: "danger",
      manual: "default"
    }
    return variants[status] || "default"
  }

  getStatusText(status) {
    if (this.statusTextsValue[status]) {
      return this.statusTextsValue[status]
    }
    return status
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  getCsrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ""
  }
}
